import { ReactElement, MouseEventHandler, useCallback, useMemo } from 'react';
import { MenuItem, ListItemText } from '@mui/material';

import { useAccessFeatureModalContext } from '~contexts/AccessFeatureModalContext';

import { useSubscriptionFeatures, useAppSelector } from '~hooks/index';
import { selectAuth } from '~store/slices/auth';
import { isAdminUser } from '~utils/user';

import { AccessFeatureMenuItemProps } from './types';
import AccessFeatureNextPlanChip from '../NextPlanChip';

const AccessFeatureMenuItem = ({
  validFeature,
  availableToDate,
  onClick,
  children,
  disableTypography = false,
  disabled: disabledProps,
  ...rest
}: AccessFeatureMenuItemProps): ReactElement => {
  const { handleOpen: handleOpenAccesFeatureModal } =
    useAccessFeatureModalContext();
  const { hasAccessToFeature } = useSubscriptionFeatures();
  const { user } = useAppSelector(selectAuth);

  const isValidFeature = useMemo(
    () => hasAccessToFeature(validFeature, availableToDate),
    [validFeature, hasAccessToFeature, availableToDate],
  );

  const isAdminCurrentUser = useMemo(() => isAdminUser(user), [user]);

  const disabled = useMemo(() => {
    if (!isAdminCurrentUser) {
      return !isValidFeature;
    }

    return disabledProps;
  }, [isAdminCurrentUser, disabledProps, isValidFeature]);

  const handleClick = useCallback<MouseEventHandler<HTMLLIElement>>(
    (event) => {
      event.preventDefault();

      if (isValidFeature) {
        onClick?.(event);

        return;
      }

      if (isAdminCurrentUser) {
        handleOpenAccesFeatureModal({
          validFeature,
          validateAvailableRoutesState: false,
        });

        return;
      }
    },
    [
      isValidFeature,
      isAdminCurrentUser,
      handleOpenAccesFeatureModal,
      validFeature,
      onClick,
    ],
  );

  const content = useMemo(() => {
    if (disableTypography) return children;

    return <ListItemText>{children}</ListItemText>;
  }, [children, disableTypography]);

  return (
    <MenuItem {...rest} onClick={handleClick} disabled={disabled}>
      {content}

      <AccessFeatureNextPlanChip
        validFeature={validFeature}
        availableToDate={availableToDate}
      />
    </MenuItem>
  );
};

export default AccessFeatureMenuItem;
